import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { createClassroom } from '../lib/supabaseClassroomAPI';

export default function CreateClassroom() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [created, setCreated] = useState(null); // { id, name, code }
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  const canSubmit = useMemo(() => name.trim().length >= 3, [name]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!canSubmit || !user?.id) return;

    setLoading(true);
    setError('');
    setCreated(null);
    setCopied(false);

    try {
      const result = await createClassroom(name.trim(), user.id);
      setCreated(result);
      setName('');
    } catch (err) {
      console.error(err);
      setError('Failed to create classroom. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!created?.code) return;
    try {
      await navigator.clipboard.writeText(created.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // no-op
    }
  };

  if (!user) return null;

  // Split the code for display cells
  const displayCells = Array.from({ length: 6 }, (_, i) => created?.code?.[i] || '');

  return (
    <div className="max-w-3xl mx-auto py-10 px-4">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Create a Classroom</h1>
        <p className="mt-2 text-gray-600">
          Create a new class space, then share the join code with students.
        </p>
      </div>

      {/* Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8 space-y-8">
        {/* Form */}
        <form onSubmit={handleCreate} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Classroom Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Grade 9 Debaters"
              className="w-full p-3 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Use something students will easily recognize.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={!canSubmit || loading}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-lg disabled:opacity-50"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4A4 4 0 004 12z"
                    />
                  </svg>
                  Creating…
                </>
              ) : (
                'Create Classroom'
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                setName('');
                setError('');
              }}
              className="text-gray-700 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg"
            >
              Clear
            </button>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
              {error}
            </div>
          )}
        </form>

        {/* Success block */}
        {created && (
          <div className="space-y-4">
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-green-800">
              Classroom <strong>{created.name}</strong> created successfully.
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Join Code</p>
              <div className="flex gap-2">
                {displayCells.map((ch, idx) => (
                  <div
                    key={idx}
                    className="w-12 h-12 sm:w-14 sm:h-14 inline-flex items-center justify-center rounded-lg border text-xl font-semibold tracking-widest border-green-500 text-gray-900 bg-green-50"
                  >
                    {ch}
                  </div>
                ))}
              </div>

              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg"
                >
                  {copied ? (
                    <>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293A1 1 0 003.293 10.707l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z" />
                      </svg>
                      Copied
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M16 1H4a2 2 0 00-2 2v12h2V3h12V1zm3 4H8a2 2 0 00-2 2v16h13a2 2 0 002-2V7a2 2 0 00-2-2zm0 18H8V7h11v16z" />
                      </svg>
                      Copy Code
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => navigate(`/classrooms/manage/${created.id}`)}
                  className="text-blue-700 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg"
                >
                  Manage Classroom
                </button>

                <button
                  type="button"
                  onClick={() => navigate(`/classrooms/student/${created.id}`)}
                  className="text-gray-700 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg"
                >
                  View as Student
                </button>
              </div>

              <p className="mt-2 text-xs text-gray-500">
                Share this code with students so they can join from the Join page.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
